import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FolderOpen, Calendar, User } from 'lucide-react';
import type { ProjectDto, ListProjectsQuery } from '@cutlyft/zod-schemas';
import { useProjects } from '@/hooks/projects/use-projects';
import { CreateProjectModal } from './components/create-project-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Deleted', value: 'deleted' },
  { label: 'All', value: 'all' },
] as const;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function ProjectCard({ project, onClick }: { project: ProjectDto; onClick: () => void }) {
  return (
    <Card
      className="cursor-pointer border border-border/50 bg-bg-elevated/80 shadow-sm hover:shadow-md hover:border-primary/40 transition-all duration-150"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base font-semibold text-fg-primary truncate">
              {project.name}
            </CardTitle>
            {project.reference && (
              <Badge variant="outline" className="mt-1 text-xs font-mono">
                {project.reference}
              </Badge>
            )}
          </div>
          {project.isDeleted === 1 && (
            <Badge variant="destructive" className="shrink-0 text-xs">
              Deleted
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-1 text-sm text-fg-secondary">
        {project.clientName && (
          <div className="flex items-center gap-1.5">
            <User className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{project.clientName}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 shrink-0" />
          <span>{formatDate(project.createdDate)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function ProjectsListSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i} className="border border-border/50">
          <CardHeader className="pb-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/3 mt-1" />
          </CardHeader>
          <CardContent className="space-y-1.5">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-1/3" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [statusFilter, setStatusFilter] =
    React.useState<NonNullable<ListProjectsQuery['status']>>('active');
  const [cursor, setCursor] = React.useState<number | undefined>(undefined);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  React.useEffect(() => {
    setCursor(undefined);
  }, [debouncedSearch, statusFilter]);

  const query: Partial<ListProjectsQuery> = {
    ...(debouncedSearch && { search: debouncedSearch }),
    status: statusFilter,
    ...(cursor && { cursor }),
    limit: 18,
  };

  const { data, isLoading, isError } = useProjects(query);

  const handleCreated = (projectId: number) => {
    navigate(`/projects/${projectId}`);
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-fg-primary">Projects</h1>
          <p className="text-fg-secondary">
            Manage window fabrication projects and their cutting calculations.
          </p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)} className="shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          New Project
        </Button>
      </div>

      {/* Search + status filter */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" />
          <Input
            className="pl-9"
            placeholder="Search by name or reference…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search projects"
          />
        </div>
        <div className="flex gap-1 rounded-md border border-border bg-bg-elevated p-1">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setStatusFilter(opt.value)}
              className={[
                'rounded px-3 py-1 text-sm font-medium transition-colors',
                statusFilter === opt.value
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-fg-secondary hover:text-fg-primary',
              ].join(' ')}
              aria-pressed={statusFilter === opt.value}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {isLoading && <ProjectsListSkeleton />}

      {isError && (
        <Card className="border-dashed">
          <CardContent className="flex h-32 items-center justify-center text-fg-muted text-sm">
            Failed to load projects. Please refresh the page.
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && data?.items?.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex h-48 flex-col items-center justify-center gap-3 text-center">
            <FolderOpen className="h-10 w-10 text-fg-muted" />
            <div>
              <p className="font-medium text-fg-primary">No projects found</p>
              <p className="text-sm text-fg-muted">
                {debouncedSearch
                  ? 'Try a different search term.'
                  : 'Create your first project to get started.'}
              </p>
            </div>
            {!debouncedSearch && (
              <Button size="sm" onClick={() => setIsCreateOpen(true)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                New Project
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && data && data.items.length > 0 && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.items.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onClick={() => navigate(`/projects/${project.id}`)}
              />
            ))}
          </div>

          {data.hasMore && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  if (data.nextCursor != null) setCursor(data.nextCursor);
                }}
              >
                Load more
              </Button>
            </div>
          )}
        </>
      )}

      <CreateProjectModal
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onCreated={handleCreated}
      />
    </div>
  );
}
